import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../hooks/useTheme';

interface TurnstileApi {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  scriptPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile indisponible'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

interface TurnstileWidgetProps {
  siteKey: string;
  action: 'login' | 'register' | 'session_check';
  onToken: (token: string | null) => void;
  
  resetKey: number;
}

function fitTurnstile(container: HTMLElement) {
  const iframe = container.querySelector('iframe');
  if (!iframe) return;
  const available = container.clientWidth;
  const natural = iframe.offsetWidth;
  if (available <= 0 || natural <= 0) return;
  const scale = Math.min(1, available / natural);
  if (scale < 0.995) {
    iframe.style.transformOrigin = 'top left';
    iframe.style.transform = `scale(${scale})`;
    container.style.height = `${Math.ceil(iframe.offsetHeight * scale)}px`;
  } else {
    iframe.style.transform = '';
    container.style.height = '';
  }
}

export function TurnstileWidget({ siteKey, action, onToken, resetKey }: TurnstileWidgetProps) {
  const { t, i18n } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const tokenCallback = useRef(onToken);
  tokenCallback.current = onToken;
  const { theme } = useTheme();

  useEffect(() => {
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !container.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(container.current, {
          sitekey: siteKey,
          action,
          theme,
          size: 'flexible',
          appearance: 'always',
          language: i18n.language.startsWith('en') ? 'en' : 'fr',
          callback: (token: string) => tokenCallback.current(token),
          'expired-callback': () => tokenCallback.current(null),
          'error-callback': () => tokenCallback.current(null),
        });
      })
      .catch(() => tokenCallback.current(null));
    return () => {
      cancelled = true;
      if (widgetId.current) window.turnstile?.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [siteKey, action, theme, i18n.language]);

  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const fit = () => fitTurnstile(element);
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    const mutations = new MutationObserver(fit);
    mutations.observe(element, { childList: true, subtree: true });
    const timers = [0, 80, 240, 700].map((delay) => window.setTimeout(fit, delay));
    return () => {
      observer.disconnect();
      mutations.disconnect();
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [siteKey, action, theme, resetKey]);

  useEffect(() => {
    if (resetKey > 0 && widgetId.current) {
      window.turnstile?.reset(widgetId.current);
      tokenCallback.current(null);
    }
  }, [resetKey]);

  return (
    <div className="turnstile-field">
      <p className="turnstile-field__label">{t('turnstile.label')}</p>
      <div ref={container} className="turnstile" />
    </div>
  );
}
